import { useMemo } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import React from 'react';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { usePagination } from 'apps/sigma2-ui/src/lib/hooks/usePagination';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export interface DataSetCompositeRecipeDataWizardTrackerItem<T extends string> {
  value: T;
  display: string;
}

export type UseDataSetCompositeRecipeDataWizardTrackerPayload<
  T extends string,
> = {
  items: DataSetCompositeRecipeDataWizardTrackerItem<T>[];
  selectedItem: T | null;
  paginationButtons: React.ReactNode;
};

export const useDataSetCompositeRecipeDataWizardTracker = <T extends string>({
  items,
  onNext,
}: {
  items: DataSetCompositeRecipeDataWizardTrackerItem<T>[];
  onNext: (currentItem: T) => boolean;
}): UseDataSetCompositeRecipeDataWizardTrackerPayload<T> => {
  const { currentPageIndex, onPreviousPageClick, onNextPageClick } =
    usePagination({
      totalPages: items.length,
    });

  const selectedItem = useMemo(() => {
    if (items.length === 0) {
      return null;
    }

    return items[currentPageIndex].value;
  }, [items, currentPageIndex]);

  const isFirstItemSelected = useMemo(() => {
    return currentPageIndex === 0;
  }, [currentPageIndex]);

  const isLastItemSelected = useMemo(() => {
    return currentPageIndex === items.length - 1;
  }, [currentPageIndex, items]);

  const paginationButtons = useMemo(() => {
    return (
      <Spacing size={'md'} direction={'horizontal'}>
        {!isFirstItemSelected && (
          <Button
            variant={'primary'}
            size={'small'}
            behavior={{
              type: 'onClick',
              onClick: () => {
                onPreviousPageClick();
              },
            }}
            text={'Previous'}
          />
        )}

        {!isLastItemSelected && (
          <Button
            variant={'primary'}
            size={'small'}
            behavior={{
              type: 'onClick',
              onClick: () => {
                const item = items.at(currentPageIndex);

                if (!item) {
                  return;
                }

                const shouldContinue = onNext(item.value);

                if (shouldContinue) {
                  onNextPageClick();
                }
              },
            }}
            text={'Next'}
          />
        )}
      </Spacing>
    );
  }, [
    isFirstItemSelected,
    isLastItemSelected,
    onPreviousPageClick,
    items,
    currentPageIndex,
    onNext,
    onNextPageClick,
  ]);

  return {
    items,
    selectedItem,
    paginationButtons,
  };
};

export const DataSetCompositeRecipeDataWizardTracker = <T extends string>({
  payload,
  showLast = false,
}: {
  payload: UseDataSetCompositeRecipeDataWizardTrackerPayload<T>;
  showLast?: boolean;
}) => {
  const language = useLanguage();

  const items = useMemo(() => {
    return payload.items;
  }, [payload.items]);

  const selectedItemTitle = useMemo(() => {
    return items.find((item) => item.value === payload.selectedItem)?.display;
  }, [items, payload]);

  const currentPageIndex = useMemo(() => {
    return items.findIndex((item) => item.value === payload.selectedItem) ?? -1;
  }, [items, payload.selectedItem]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <Spacing
        size={'lg'}
        direction={'horizontal'}
        as={'div'}
        className={'self-center'}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isPassed = index < currentPageIndex;

          if (!showLast && isLast) {
            return null;
          }

          return (
            <Spacing
              size={'md'}
              direction={'vertical'}
              as={'div'}
              className={'items-center'}
              key={index}
            >
              <Box
                as={'div'}
                p={'xs'}
                rounded={'full'}
                bg={isPassed ? 'green-300' : 'white'}
                key={index}
                display={'flex'}
                flexDirection={'col'}
                justifyContent={'center'}
                alignItems={'center'}
                h={'h-[48px]'}
                w={'w-[48px]'}
              >
                <Box>
                  <S2Typography
                    variant={'headline-3'}
                    translatedContent={language.dangerousUserInput({
                      value: `${index + 1}`,
                    })}
                  />
                </Box>
              </Box>

              <S2Typography
                variant={isPassed ? 'small-label-alt' : 'small-label'}
                textColorVariant={'gray-1000'}
                translatedContent={language.dangerousUserInput({
                  value: item.display,
                })}
              />
            </Spacing>
          );
        })}
      </Spacing>

      <S2Typography
        variant={'headline-5-alt'}
        translatedContent={language.dangerousUserInput({
          value: selectedItemTitle ?? '',
        })}
      />
    </Spacing>
  );
};
